/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaLayerGroup, 
  FaGlobe, 
  FaCheckCircle, 
  FaGripVertical, 
  FaImages, 
  FaColumns, 
  FaThLarge, 
  FaRegFileAlt, 
  FaPlus,
  FaStar,
  FaComments,
  FaTrash
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './EditHome.css';

const EditHome = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('layout'); // 'layout' or 'seo'
  const [loading, setLoading] = useState(true);

  // Layout State: holds the array of active sections for the homepage
  const [layoutList, setLayoutList] = useState([]);
  // All available widget definitions from widget_section table
  const [availableWidgets, setAvailableWidgets] = useState([]);

  // SEO State
  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });

  // Modal states for confirm remove and adding widgets
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, name: '' });
  const [addWidgetModal, setAddWidgetModal] = useState(false);
  const [selectedWidgetToAdd, setSelectedWidgetToAdd] = useState('');

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pages/home');
      if (res && res.data && res.data.success) {
        const { page, widgets } = res.data;
        
        // Populate Available Widgets from DB
        if (widgets && Array.isArray(widgets)) {
          setAvailableWidgets(widgets);
        }

        if (page) {
          // Parse description JSON for layout structure
          let parsedLayout = [];
          if (page.description) {
            try {
              parsedLayout = typeof page.description === 'string' ? JSON.parse(page.description) : page.description;
            } catch (e) {
              console.error("Error parsing page layout description:", e);
            }
          }

          // Filter to only active items or load default ones from screenshot if empty
          if (Array.isArray(parsedLayout) && parsedLayout.length > 0) {
            setLayoutList(parsedLayout);
          } else {
            // Default layout match if brand new
            setLayoutList([
              { id: "0", section_id: "1", status: "1", sort: 1 },
              { id: "1", section_id: "4", status: "1", sort: 2 },
              { id: "8", section_id: "9", status: "1", sort: 3 }
            ]);
          }

          // Populate SEO Fields
          setSeo({
            meta_title: page.meta_title || 'Best Luxury Wardrobe & Modular Kitchen Brand In India | Aristo',
            meta_description: page.meta_description || "Aristo is India's leading luxury wardrobe and modular kitchen manufacturers, offering premium interior solutions for stylish bedrooms and kitchens.",
            meta_keywords: page.meta_keywords || ''
          });
        }
      }
    } catch (err) {
      console.error("Error fetching Home data:", err);
      toast.error("Failed to load Home Page configuration");
    } finally {
      setLoading(false);
    }
  };

  // Helper to map widget section ID to display title and icons
  const getWidgetInfo = (sectionId) => {
    const found = availableWidgets.find(w => String(w.id) === String(sectionId));
    const title = found ? found.section : `Section Widget #${sectionId}`;
    
    // Pick appropriate React icon based on section title
    const lowerTitle = title.toLowerCase();
    let IconComponent = FaRegFileAlt;
    if (lowerTitle.includes('slider') || lowerTitle.includes('banner') || lowerTitle.includes('image')) {
      IconComponent = FaImages;
    } else if (lowerTitle.includes('content') || lowerTitle.includes('right') || lowerTitle.includes('left')) {
      IconComponent = FaColumns;
    } else if (lowerTitle.includes('solution') || lowerTitle.includes('tile') || lowerTitle.includes('grid')) {
      IconComponent = FaThLarge;
    } else if (lowerTitle.includes('testimonial')) {
      IconComponent = FaComments;
    } else if (lowerTitle.includes('highlight')) {
      IconComponent = FaStar;
    }

    return { title, IconComponent };
  };

  // Handle Remove Trigger
  const handleRemoveClick = (e, idx, title) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, index: idx, name: title });
  };

  const confirmDelete = () => {
    if (deleteModal.index !== null) {
      const newList = layoutList.filter((_, i) => i !== deleteModal.index);
      setLayoutList(newList);
      toast.success(`Removed "${deleteModal.name}" from Layout Manager`);
    }
    setDeleteModal({ isOpen: false, index: null, name: '' });
  };

  // Handle Add Widget
  const handleAddWidgetSubmit = (e) => {
    e.preventDefault();
    if (!selectedWidgetToAdd) {
      toast.warning('Please select a section to add!');
      return;
    }
    const newIdx = layoutList.length + 1;
    const newItem = {
      id: String(newIdx),
      section_id: String(selectedWidgetToAdd),
      status: "1",
      sort: newIdx
    };
    setLayoutList([...layoutList, newItem]);
    const { title } = getWidgetInfo(selectedWidgetToAdd);
    toast.success(`Added "${title}" to Layout!`);
    setAddWidgetModal(false);
    setSelectedWidgetToAdd('');
  };

  // Save changes to Database
  const handleSave = async () => {
    try {
      const payload = {
        description: layoutList,
        meta_title: seo.meta_title,
        meta_description: seo.meta_description,
        meta_keywords: seo.meta_keywords
      };

      const res = await axiosInstance.patch('/pages/home', payload);
      if (res.data && res.data.success) {
        toast.success('Home Page saved successfully!');
      } else {
        toast.error(res.data.message || 'Error updating Home Page');
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error('Server error while saving Home Page configuration');
    }
  };

  if (loading) {
    return (
      <div className="edit-home-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading Home Configuration...</h3>
      </div>
    );
  }

  return (
    <div className="edit-home-container animate-fade-up">
      {/* Header */}
      <div className="edit-home-header">
        <h1 className="edit-home-title">Home</h1>
      </div>

      {/* Main Content Grid: Fixed Sidebar & Scrollable Right Panel */}
      <div className="edit-home-grid">
        
        {/* Sticky Left Sidebar Navigation */}
        <div className="edit-home-sidebar">
          <ul className="edit-home-nav-list">
            <li 
              className={`edit-home-nav-item ${activeTab === 'layout' ? 'active' : ''}`}
              onClick={() => setActiveTab('layout')}
            >
              <div className="edit-home-nav-left">
                <span className="edit-home-nav-icon"><FaLayerGroup /></span>
                <span>Layout Manager</span>
              </div>
              <span className="edit-home-status-icon"><FaCheckCircle /></span>
            </li>
            
            <li 
              className={`edit-home-nav-item ${activeTab === 'seo' ? 'active' : ''}`}
              onClick={() => setActiveTab('seo')}
            >
              <div className="edit-home-nav-left">
                <span className="edit-home-nav-icon"><FaGlobe /></span>
                <span>SEO Manager</span>
              </div>
              <span className="edit-home-status-icon"><FaCheckCircle /></span>
            </li>
          </ul>
        </div>

        {/* Right Scrollable Content Panel */}
        <div className="edit-home-content-panel">
          
          {/* TAB 1: LAYOUT MANAGER */}
          {activeTab === 'layout' && (
            <div className="tab-pane-layout animate-fade-up">
              <h2 className="edit-home-panel-title">Layout Manager</h2>
              
              <div className="layout-manager-list">
                {layoutList && layoutList.length > 0 ? (
                  layoutList.map((item, index) => {
                    const { title, IconComponent } = getWidgetInfo(item.section_id);
                    return (
                      <div key={index} className="layout-widget-card">
                        <div className="widget-left-area">
                          <span className="widget-drag-handle" title="Reorder Section"><FaGripVertical /></span>
                          <div className="widget-icon-wrap">
                            <IconComponent />
                          </div>
                          <h4 className="widget-title-text">{title}</h4>
                        </div>
                        
                        <button 
                          type="button" 
                          className="widget-remove-btn"
                          onClick={(e) => handleRemoveClick(e, index, title)}
                          title={`Remove ${title}`}
                        >
                          <FaTrash className="widget-remove-icon" />
                          <span>Remove</span>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <p style={{ color: '#94a3b8', textAlign: 'center', margin: '2rem 0' }}>
                    No active layout sections configured. Add one below!
                  </p>
                )}
              </div>

              {/* Add More Sections Action */}
              <div className="add-widget-block">
                <button 
                  type="button" 
                  className="add-widget-btn"
                  onClick={() => setAddWidgetModal(true)}
                >
                  <FaPlus /> <span>Add Section to Layout</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: SEO MANAGER */}
          {activeTab === 'seo' && (
            <div className="tab-pane-seo animate-fade-up">
              <h2 className="edit-home-panel-title">SEO Manager</h2>
              
              <div className="seo-form-group">
                <label className="seo-label">
                  Meta Title <span className="req">*</span>
                </label>
                <input 
                  type="text" 
                  className="seo-input"
                  placeholder="Enter meta title..."
                  value={seo.meta_title}
                  onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })}
                />
              </div>

              <div className="seo-form-group">
                <label className="seo-label">
                  Meta Description <span className="req">*</span>
                </label>
                <textarea 
                  className="seo-textarea"
                  rows={4}
                  placeholder="Enter meta description..."
                  value={seo.meta_description}
                  onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
                />
              </div>

              <div className="seo-form-group">
                <label className="seo-label">
                  Meta Keywords
                </label>
                <input 
                  type="text" 
                  className="seo-input"
                  placeholder="Enter keywords..."
                  value={seo.meta_keywords}
                  onChange={(e) => setSeo({ ...seo, meta_keywords: e.target.value })}
                />
                <span className="seo-helper-text">Add commas to separate keywords</span>
              </div>
            </div>
          )}

        </div>

      </div>

      {/* Footer Action Bar */}
      <div className="edit-home-footer">
        <button 
          type="button" 
          className="btn-cancel"
          onClick={() => navigate('/page-list')}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="btn-save"
          onClick={handleSave}
        >
          Save
        </button>
      </div>

      {/* Delete Confirmation Modal (Avoids Bootstrap .modal-content clash!) */}
      {deleteModal.isOpen && (
        <div 
          className="modal-overlay" 
          onClick={(e) => e.stopPropagation()} 
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="premium-confirm-modal-card">
            <h3>Remove Layout Widget?</h3>
            <p>
              Are you sure you want to remove <strong>&quot;{deleteModal.name}&quot;</strong> from the homepage layout?
            </p>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={() => setDeleteModal({ isOpen: false, index: null, name: '' })}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-delete-confirm" 
                onClick={confirmDelete}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Widget Modal */}
      {addWidgetModal && (
        <div 
          className="modal-overlay" 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="premium-confirm-modal-card" style={{ maxWidth: '480px', textAlign: 'left' }}>
            <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.5rem' }}>
              Add Layout Section
            </h3>
            <form onSubmit={handleAddWidgetSubmit}>
              <div style={{ margin: '1.5rem 0' }}>
                <label className="seo-label">Select Widget / Section:</label>
                <select 
                  className="seo-input" 
                  style={{ background: '#0f172a' }}
                  value={selectedWidgetToAdd}
                  onChange={(e) => setSelectedWidgetToAdd(e.target.value)}
                >
                  <option value="">-- Choose Section --</option>
                  {availableWidgets && availableWidgets.map((widget) => (
                    <option key={widget.id} value={widget.id}>
                      {widget.section}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={() => setAddWidgetModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save"
                  style={{ padding: '0.5rem 1.5rem', margin: 0 }}
                >
                  Add Section
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default EditHome;
